import React, { useState, useEffect } from 'react';
import { Grid, Stack, Typography, Button, TextField, FormControlLabel, Checkbox } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useGlobalStyles } from 'Components/Common/global/global';
import { Close, AddTask } from '@mui/icons-material';
import useLifeExpectancyAdjustment from 'hooks/useLifeExpectancyAdjustment';
import BasicDatePicker from 'Components/Reusable/DatePicker';
import useUserActions from 'hooks/useUserActions';

export interface LifeExpectancyValues {
	id?: number;
	dateFrom?: string;
	dateTo?: string;
	adjRate?: number;
	adjMale?: number;
	adjFemale?: number;
	unisex?: boolean;
	isCurrent?: boolean;
}

interface P {
	selectedEditData?: LifeExpectancyValues;
	handleClose: () => void;
	reload?: boolean;
	setReload?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateEditLifeEcpectancy = ({ selectedEditData, handleClose, reload, setReload }: P) => {
	const { handleRoles } = useUserActions();
	const global = useGlobalStyles();
	const { createLifeExpectancyAdjustment, updateLifeExpectancyAdjustment } = useLifeExpectancyAdjustment();
	const [formValues, setFormValues] = useState<LifeExpectancyValues>({});
	const [saveLoading, setSaveLoading] = useState(false);
	const [formErrors, setFormErrors] = useState<string[]>([]);

	useEffect(() => {
		if (!selectedEditData) {
			setFormValues({});
		} else {
			const { dateFrom, dateTo, adjRate, adjMale, adjFemale, unisex, isCurrent } = selectedEditData;
			setFormValues({
				dateFrom,
				dateTo,
				adjRate,
				adjMale,
				adjFemale,
				unisex,
				isCurrent,
			});
		}
	}, [selectedEditData]);

	const handleSubmit = async () => {
		const errors = [...formErrors];
		if (!formValues.adjRate) {
			errors.push('adjRate');
		}
		if (!formValues.dateFrom) {
			errors.push('dateFrom');
		}
		if (!formValues.dateTo) {
			errors.push('dateTo');
		}
		setFormErrors(errors);
		if (errors.length) return;

		setSaveLoading(true);
		let isSucess;
		if (selectedEditData?.id) {
			isSucess = await updateLifeExpectancyAdjustment(formValues, selectedEditData.id);
		} else {
			isSucess = await createLifeExpectancyAdjustment(formValues);
		}
		setSaveLoading(false);
		handleClose();
		if (isSucess) {
			setReload?.(!reload);
		}
	};

	const handleChange = (name: string, value: string | boolean) => {
		if ((name === 'adjRate' || name === 'adjFemale' || name === 'adjMale') && typeof value === 'string') {
			setFormValues({
				...formValues,
				[name]: parseFloat(value),
			});
		} else {
			setFormValues({
				...formValues,
				[name]: value,
			});
		}
		if (formErrors.includes(name)) {
			setFormErrors(formErrors.filter((item) => item !== name));
		}
	};

	return (
		<Stack mt={2}>
			<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 4 }}>
				<Grid item xs={12} md={6} mb={2}>
					<Grid container>
						<Typography variant="body2" className={global.formLabelText}>
							Date From
						</Typography>
					</Grid>
					<BasicDatePicker
						size="small"
						value={formValues.dateFrom}
						setValue={(e) => handleChange('dateFrom', e.target.value)}
					/>
					{formErrors.includes('dateFrom') && <span className={global.errorText}>Date From is required</span>}
				</Grid>
				<Grid item xs={12} md={6} mb={2}>
					<Grid container>
						<Typography variant="body2" className={global.formLabelText}>
							Date To
						</Typography>
					</Grid>
					<BasicDatePicker
						size="small"
						value={formValues.dateTo}
						setValue={(e) => handleChange('dateTo', e.target.value)}
					/>
					{formErrors.includes('dateTo') && <span className={global.errorText}>Date To is required</span>}
				</Grid>
				<Grid item xs={12} md={6} mb={2}>
					<Grid container>
						<Typography variant="body2" className={global.formLabelText}>
							Adjustment Rate
						</Typography>
					</Grid>
					<TextField
						size="small"
						type="number"
						value={formValues.adjRate}
						onChange={(e) => handleChange('adjRate', e.target.value)}
						fullWidth
					/>
					{formErrors.includes('adjRate') && (
						<span className={global.errorText}>Adjustment rate is required</span>
					)}
				</Grid>
				<Grid item xs={12} md={6} mb={2}>
					<Grid container>
						<Typography variant="body2" className={global.formLabelText}>
							Male Adjustment Rate
						</Typography>
					</Grid>
					<TextField
						size="small"
						type="number"
						value={formValues.adjMale}
						onChange={(e) => handleChange('adjMale', e.target.value)}
						fullWidth
					/>
				</Grid>
				<Grid item xs={12} md={6} mb={2}>
					<Grid container>
						<Typography variant="body2" className={global.formLabelText}>
							Female Adjustment Rate
						</Typography>
					</Grid>
					<TextField
						size="small"
						type="number"
						value={formValues.adjFemale}
						onChange={(e) => handleChange('adjFemale', e.target.value)}
						fullWidth
					/>
				</Grid>
				<Grid item xs={12} md={6} mb={2}>
					<FormControlLabel
						value="top"
						control={
							<Checkbox
								checked={formValues.unisex ?? false}
								onChange={(e) => handleChange('unisex', e.target.checked)}
							/>
						}
						label="Unisex"
					/>
				</Grid>
			</Grid>
			{handleRoles('annuities-actions') && (
				<Stack direction="row" justifyContent="center" py={3} spacing={2}>
					<LoadingButton
						loadingPosition="start"
						loading={saveLoading}
						startIcon={<AddTask />}
						sx={{ textTransform: 'none' }}
						onClick={handleSubmit}
						variant="contained"
					>
						Save
					</LoadingButton>
					<Button
						sx={{ textTransform: 'none' }}
						startIcon={<Close />}
						onClick={handleClose}
						variant="outlined"
						className={global.whiteBtn}
					>
						Cancel
					</Button>
				</Stack>
			)}
		</Stack>
	);
};

export default CreateEditLifeEcpectancy;
